import { z } from 'zod'
import { aggregateFunctionSchema, queryRunFilterSchema } from './query.js'

// ---------------------------------------------------------------------------
// Ask — the natural-language-to-query-spec translator.
//
// Supersedes the earlier, narrower Ask contract (filter | fallback |
// cap_reached). The load-bearing rules are unchanged and non-negotiable:
//
//   1. The LLM never sees query results. It sees the question, the vocabulary
//      pack, and the classification rules. Nothing else.
//   2. The LLM never generates interpretive prose about data. The one prose
//      field it writes — `explanation` — is template-constrained per reason
//      code, and is *about the refusal*, never about the data.
//   3. Every rendered output is a real query against live data, carrying the
//      `as_of` of the execution that produced it.
//
// What changed is the *shape* of what the LLM may produce: seven outcomes
// instead of three, six of which are answerable query shapes or a routing
// decision, and one of which is an honest refusal with suggested alternatives.
//
// `filter`s here are the CONTRACT's `RunFilter` (snake_case, array-valued
// verdict) — not the console's internal Runs-page filter. See `query.ts`.
// ---------------------------------------------------------------------------

export const askRequestSchema = z.object({
  prompt: z.string().min(1).max(500),
})
export type AskRequest = z.infer<typeof askRequestSchema>

export const askFilterResultSchema = z
  .object({
    kind: z.literal('filter'),
    filter: queryRunFilterSchema,
    limit: z.number().int().positive().max(1000).optional(),
    /**
     * ADDITIVE TO SPEC §2. §5.7 requires a refusal's suggestions to be
     * clickable buttons that populate the Ask box with 'the LLM's phrasing of
     * the question' — which needs that phrasing to exist somewhere, and §2's
     * result types have no field for it. Optional, so every shape in §2 stays
     * valid unchanged; set only on suggestions. Flagged for joint review.
     */
    question: z.string().optional(),
  })
  .strict()
export type AskFilterResult = z.infer<typeof askFilterResultSchema>

export const askAggregateResultSchema = z
  .object({
    kind: z.literal('aggregate'),
    filter: queryRunFilterSchema,
    function: aggregateFunctionSchema,
    /** Required for every function except `count` — checked below, and again against the vocabulary's `aggregatable` flag at validation time. */
    field: z.string().optional(),
    groupBy: z.string().optional(),
    /**
     * ADDITIVE TO SPEC §2. §5.7 requires a refusal's suggestions to be
     * clickable buttons that populate the Ask box with 'the LLM's phrasing of
     * the question' — which needs that phrasing to exist somewhere, and §2's
     * result types have no field for it. Optional, so every shape in §2 stays
     * valid unchanged; set only on suggestions. Flagged for joint review.
     */
    question: z.string().optional(),
  })
  .strict()
  .refine((r) => r.function === 'count' || r.field !== undefined, {
    message: 'field is required for every aggregate function except count',
    path: ['field'],
  })
export type AskAggregateResult = z.infer<typeof askAggregateResultSchema>

export const askBreakdownResultSchema = z
  .object({
    kind: z.literal('breakdown'),
    filter: queryRunFilterSchema,
    groupBy: z.string(),
    orderBy: z.enum(['count', 'sum']),
    orderByField: z.string().optional(),
    topN: z.number().int().positive().optional(),
    /**
     * ADDITIVE TO SPEC §2. §5.7 requires a refusal's suggestions to be
     * clickable buttons that populate the Ask box with 'the LLM's phrasing of
     * the question' — which needs that phrasing to exist somewhere, and §2's
     * result types have no field for it. Optional, so every shape in §2 stays
     * valid unchanged; set only on suggestions. Flagged for joint review.
     */
    question: z.string().optional(),
  })
  .strict()
  .refine((r) => r.orderBy !== 'sum' || r.orderByField !== undefined, {
    message: 'orderByField is required when orderBy is "sum"',
    path: ['orderByField'],
  })
export type AskBreakdownResult = z.infer<typeof askBreakdownResultSchema>

export const askTimelineResultSchema = z
  .object({
    kind: z.literal('timeline'),
    filter: queryRunFilterSchema,
    granularity: z.enum(['day', 'week', 'month']),
    /**
     * ADDITIVE TO SPEC §2. §5.7 requires a refusal's suggestions to be
     * clickable buttons that populate the Ask box with 'the LLM's phrasing of
     * the question' — which needs that phrasing to exist somewhere, and §2's
     * result types have no field for it. Optional, so every shape in §2 stays
     * valid unchanged; set only on suggestions. Flagged for joint review.
     */
    question: z.string().optional(),
  })
  .strict()
export type AskTimelineResult = z.infer<typeof askTimelineResultSchema>

/**
 * No nested compare, and no `cannot_answer` inside a compare. Both are the
 * spec's constraints, and both are enforced by this type rather than at
 * runtime: comparing comparisons has no clear reading, and a refusal is not a
 * thing you can put side-by-side with a number.
 */
export const askCompareResultSchema = z
  .object({
    kind: z.literal('compare'),
    queries: z
      .array(z.union([askFilterResultSchema, askAggregateResultSchema, askBreakdownResultSchema]))
      .min(2),
  })
  .strict()
export type AskCompareResult = z.infer<typeof askCompareResultSchema>

export const askRouteToViewSchema = z
  .object({
    kind: z.literal('route_to_view'),
    view: z.enum(['run_detail', 'health_breakdown', 'runs_filtered', 'rules']),
    params: z.record(z.string(), z.string()),
    /** Template-constrained: says why this is the right destination, never what the data means. */
    reason: z.string().min(1),
  })
  .strict()
export type AskRouteToViewResult = z.infer<typeof askRouteToViewSchema>

/**
 * The six honest ways to not answer. `unsupported_aggregation` was considered
 * and deliberately folded into `vocabulary_gap`: the vocabulary is what
 * determines which aggregations are meaningful, so "we don't support stddev"
 * and "this field isn't aggregatable" are the same category of fact.
 */
export const askCannotAnswerReasonSchema = z.enum([
  'vocabulary_gap',
  'causal_question',
  'judgment_question',
  'insufficient_data',
  'ambiguous_question',
  'out_of_scope',
])
export type AskCannotAnswerReason = z.infer<typeof askCannotAnswerReasonSchema>

export const askCannotAnswerSchema = z
  .object({
    kind: z.literal('cannot_answer'),
    reason: askCannotAnswerReasonSchema,
    explanation: z.string().min(1),
    /**
     * 1-3 answerable alternatives. A refusal that redirects is the design;
     * a refusal that dead-ends is the failure mode this bound exists to
     * prevent. `route_to_view` is not among them — a suggestion has to be
     * something Ask can actually run.
     */
    suggested_queries: z
      .array(
        z.union([
          askFilterResultSchema,
          askAggregateResultSchema,
          askBreakdownResultSchema,
          askTimelineResultSchema,
        ]),
      )
      .min(1)
      .max(3),
  })
  .strict()
export type AskCannotAnswerResult = z.infer<typeof askCannotAnswerSchema>

export const askResultSchema = z.union([
  askFilterResultSchema,
  askAggregateResultSchema,
  askBreakdownResultSchema,
  askTimelineResultSchema,
  askCompareResultSchema,
  askRouteToViewSchema,
  askCannotAnswerSchema,
])
export type AskResult = z.infer<typeof askResultSchema>

/** The kinds that produce a query Cadence can execute — everything except routing and refusal. */
export type AskExecutableResult =
  AskFilterResult | AskAggregateResult | AskBreakdownResult | AskTimelineResult | AskCompareResult

export const askResponseSchema = z
  .object({
    question: z.string(),
    result: askResultSchema,
  })
  .strict()
export type AskResponse = z.infer<typeof askResponseSchema>

// ---------------------------------------------------------------------------
// Fixed copy. Held here, not in the prompt, because these are the strings the
// console falls back to when the model is unavailable or unusable — the model
// cannot be the source of the text that explains the model failing.
// ---------------------------------------------------------------------------

/**
 * Returned verbatim when the LLM's output fails schema or vocabulary
 * validation. `ambiguous_question` per the spec: from the operator's side, a
 * response we couldn't parse is indistinguishable from a question we couldn't
 * pin down, and claiming a more specific reason would be inventing one.
 */
export const ASK_UNPARSEABLE_EXPLANATION =
  "That could mean more than one thing, and I couldn't pin it down. Try naming the runs you mean and what you want counted."

/** §9: cap reached. `{hours}` is substituted with whole hours until the UTC day rolls over. */
export function askCapReachedExplanation(hoursUntilReset: number): string {
  const hours = Math.max(1, Math.ceil(hoursUntilReset))
  return `Ask is temporarily unavailable — daily limit reached. It will reset in ${String(hours)} ${hours === 1 ? 'hour' : 'hours'}.`
}

export const ASK_CANNOT_ANSWER_LABEL: Record<AskCannotAnswerReason, string> = {
  vocabulary_gap: 'Vocabulary gap',
  causal_question: 'Causal question',
  judgment_question: 'Judgment question',
  insufficient_data: 'Insufficient data',
  ambiguous_question: 'Ambiguous question',
  out_of_scope: 'Out of scope',
}

export const llmAuditOutcomeSchema = z.enum(['validated', 'rejected', 'error', 'cap_reached'])
export type LlmAuditOutcome = z.infer<typeof llmAuditOutcomeSchema>
